import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { MultiSelect } from '@/components/MultiSelect';

import { Codes } from '@/constants/codes';
import moment from 'moment';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface CaseReportPersonsInvolvedProps {
  persons: any[];
  isReadOnly?: boolean;
  canSaveNpsdData?: boolean;
  onChange?: (persons: any[]) => void;
  onAddExisting?: (type: string) => void;
  onAddNew?: (type: string) => void;
  onAddUnknown?: (type: string) => void;
  onViewItem?: (type: string, id: any) => void;
  onRolesChanged?: (type: string, id: any, roles: number[]) => void;
  onRemoveItem?: (type: string, item: any) => void;
}

export function CaseReportPersonsInvolved({
  persons,
  isReadOnly = false,
  canSaveNpsdData = false,
  onChange,
  onAddExisting,
  onAddNew,
  onAddUnknown,
  onViewItem,
  onRolesChanged,
  onRemoveItem,
}: CaseReportPersonsInvolvedProps) {
  const { theme } = useTheme();
  const rootRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(persons?.length || 0);

  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: any }>({
    open: false,
    item: null,
  });

  const personRoleOptions = useMemo(() => {
    const codes = Codes.getPersonCodes ? Codes.getPersonCodes() : [];
    return codes.map((c: any) => ({ value: c.code, label: c.value }));
  }, []);

  // Watch for new persons added - scroll to last mobile card
  useEffect(() => {
    const newLength = persons?.length || 0;
    if (newLength > prevLengthRef.current) {
      setTimeout(() => {
        if (rootRef.current) {
          scrollToLastMobileCard(rootRef.current);
        }
      }, 0);
    }
    prevLengthRef.current = newLength;
  }, [persons]);

  const addExisting = useCallback(() => {
    onAddExisting?.('Person');
  }, [onAddExisting]);

  const addNew = useCallback(() => {
    onAddNew?.('Person');
  }, [onAddNew]);

  const addUnknown = useCallback(() => {
    onAddUnknown?.('Person');
  }, [onAddUnknown]);

  const viewPerson = useCallback((person: any) => {
    onViewItem?.('Person', person.id);
  }, [onViewItem]);

  const getPersonRoles = useCallback((person: any): number[] => {
    return person.connections?.roles || [];
  }, []);

  const onRolesChange = useCallback((person: any, newRoles: number[]) => {
    if (isReadOnly) return;
    onRolesChanged?.('Person', person.id, newRoles);
  }, [isReadOnly, onRolesChanged]);

  const confirmDelete = useCallback((person: any) => {
    setDeleteConfirm({ open: true, item: person });
  }, []);

  const doDelete = useCallback(() => {
    const item = deleteConfirm.item;
    if (!item) {
      setDeleteConfirm({ open: false, item: null });
      return;
    }
    onRemoveItem?.('Person', item);
    setDeleteConfirm({ open: false, item: null });
  }, [deleteConfirm.item, onRemoveItem]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, item: null });
  }, []);

  const formatPersonName = useCallback((p: any) => {
    if (p.fullName) return p.fullName;
    const parts = [p.firstName, p.middleName, p.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'Unknown Person';
  }, []);

  const formatDob = useCallback((p: any) => {
    if (!p.dob) return '';
    const m = moment.utc(p.dob);
    if (!m.isValid()) return '';
    return m.format('DD MMM YYYY');
  }, []);

  const formatGender = useCallback((p: any) => {
    if (!p.gender) return '';
    const genderCodes = Codes.getGenderCodes ? Codes.getGenderCodes() : [];
    const gender = genderCodes.find((g: any) => g.code === p.gender);
    return gender ? gender.value : '';
  }, []);

  const formatRace = useCallback((p: any) => {
    if (!p.race) return '';
    const raceCodes = Codes.getRaceCodes ? Codes.getRaceCodes() : [];
    const race = raceCodes.find((r: any) => r.code === p.race);
    return race ? race.value : '';
  }, []);

  const formatRoles = useCallback((p: any) => {
    if (!p.connections || !p.connections.roles || p.connections.roles.length === 0) {
      return '\u2014';
    }
    const roleNames = p.connections.roles.map((roleCode: number) => {
      const role = personRoleOptions.find((r: any) => r.value === roleCode);
      return role ? role.label : '';
    }).filter(Boolean);
    return roleNames.length > 0 ? roleNames.join(', ') : '\u2014';
  }, [personRoleOptions]);

  return (
    <div className={'case-report-involved-grid' + (theme ? ' ' + theme : '')} ref={rootRef}>
      <div className="header">
        <h1>Person(s) <span className="hidden-sm hidden-xs">Involved</span></h1>
        <div className="header-buttons">
          {canSaveNpsdData && !isReadOnly && (
            <button
              type="button"
              className="btn-add btn-secondary hidden-sm hidden-xs"
              onClick={addUnknown}
            >
              <i className="fas fa-plus"></i> UNKNOWN
            </button>
          )}
          {canSaveNpsdData && !isReadOnly && (
            <button
              type="button"
              className="btn-add btn-secondary"
              onClick={addNew}
            >
              <i className="fas fa-plus"></i> NEW
            </button>
          )}
          {!isReadOnly && (
            <button
              type="button"
              className="btn-add"
              onClick={addExisting}
            >
              <i className="fas fa-plus"></i> EXISTING
            </button>
          )}
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '15%' }}>Last Name</th>
              <th style={{ width: '12%' }}>First Name</th>
              <th style={{ width: '10%' }}>Middle</th>
              <th style={{ width: '12%' }}>DOB</th>
              <th style={{ width: '10%' }}>Gender</th>
              <th style={{ width: '10%' }}>Race</th>
              <th style={{ width: '20%' }}>Incident Role(s)</th>
              {!isReadOnly && <th style={{ width: '8%' }}>Actions</th>}
            </tr>
          </thead>
          {persons.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={isReadOnly ? 7 : 8} className="empty-cell">
                  No persons involved
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {persons.map((person) => (
                <tr key={person.id}>
                  <td>
                    <a href="#" className="item-link" onClick={(e) => { e.preventDefault(); viewPerson(person); }}>
                      {person.lastName || 'Unknown'}
                    </a>
                  </td>
                  <td>{person.firstName || ''}</td>
                  <td>{person.middleName || ''}</td>
                  <td>{formatDob(person)}</td>
                  <td>{formatGender(person)}</td>
                  <td>{formatRace(person)}</td>
                  <td>
                    {!isReadOnly ? (
                      <MultiSelect
                        value={getPersonRoles(person)}
                        items={personRoleOptions}
                        labelKey="label"
                        valueKey="value"
                        placeholder="Select Roles"
                        maxDisplay={2}
                        disabled={isReadOnly}
                        onChange={(newRoles) => onRolesChange(person, newRoles)}
                      />
                    ) : (
                      <span>{formatRoles(person)}</span>
                    )}
                  </td>
                  {!isReadOnly && (
                    <td>
                      <div className="actions">
                        <button className="btn-delete" onClick={() => confirmDelete(person)} title="Remove person">
                          Remove
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      <div className="mobile-cards">
        {persons.length === 0 ? (
          <div className="empty-state">
            <p>No persons involved</p>
          </div>
        ) : (
          persons.map((person) => (
            <div key={'mobile-' + person.id} className="card">
              <div className="card-header">
                <div className="card-header-content">
                  <a href="#" className="card-item-name" onClick={(e) => { e.preventDefault(); viewPerson(person); }}>
                    {formatPersonName(person)}
                  </a>
                </div>
                {!isReadOnly && (
                  <button className="btn-delete" onClick={() => confirmDelete(person)}>Remove</button>
                )}
              </div>

              <div className="card-grid">
                <div className="card-field">
                  <div className="card-field-label">DOB</div>
                  <div className="card-field-value">{formatDob(person) || '\u2014'}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Gender</div>
                  <div className="card-field-value">{formatGender(person) || '\u2014'}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Race</div>
                  <div className="card-field-value">{formatRace(person) || '\u2014'}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Roles</div>
                  <div className="card-field-value">
                    {!isReadOnly ? (
                      <MultiSelect
                        value={getPersonRoles(person)}
                        items={personRoleOptions}
                        labelKey="label"
                        valueKey="value"
                        placeholder="Select Roles"
                        maxDisplay={2}
                        disabled={isReadOnly}
                        onChange={(newRoles) => onRolesChange(person, newRoles)}
                      />
                    ) : (
                      <span>{formatRoles(person)}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        open={deleteConfirm.open}
        title="Remove Person?"
        messageHtml="Are you sure you want to remove this person from the report?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="danger"
        onConfirm={doDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

(window as any).CaseReportPersonsInvolvedReact = CaseReportPersonsInvolved;
